// Package Block
import { useEffect, useMemo } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import paginationFactory, { PaginationProvider } from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";

// Custome Components & Functions Block
import * as actions from "../../../../_redux/dashboard/dashboardActions";
import { NoRecordsFoundMessage, PleaseWaitMessage, getHandlerTableChange } from "../../../../../../../_metronic/_helpers";
import { Pagination } from "../../../../../../../_metronic/_partials/controls";
import { CircularLoader } from "../../../../../Common/loader";
import { SIZE_PER_PAGE_LIST } from "../../../../../Common/constants";
import { customTotal, getSettingKey } from "../../../../../Common/utils";
import { useNotificationsUIContext } from "../NotificationsUIContext";
import { formatNotificationDateTime } from "../notificationDateUtils";

const getColumns = (timezone) => [
  {
    text: "Date | Day",
    dataField: "created_at",
    style: { width: "20%" },
    formatter: (created_at) => {
      const { date: formattedDate, day } = formatNotificationDateTime(timezone, created_at);
      return (
        <>
          {formattedDate} | {day}
        </>
      );
    },
  },
  {
    text: "Time",
    dataField: "created_at",

    formatter: (created_at) => {
      const { time: formattedTime } = formatNotificationDateTime(timezone, created_at);
      return <>{formattedTime}</>;
    },
  },
  {
    text: "Message",
    dataField: "message",
  },
];

export function NotificationTable({ id, startDate, endDate }) {
  const { currentState } = useSelector((state) => ({ currentState: state.dashboard }), shallowEqual);
  const { listLoading, notificationEntities } = currentState;
  const { totalCount } = currentState;
  const { currentBasicState } = useSelector((state) => ({ currentBasicState: state.basicSettings }), shallowEqual);
  const timezone = getSettingKey(currentBasicState.entity, "Basic", "timezone");
  const columns = useMemo(() => getColumns(timezone), [timezone]);
  const dispatch = useDispatch();
  const NotificationsUIContext = useNotificationsUIContext();

  const NotificationsUIProps = useMemo(() => {
    return {
      queryParams: NotificationsUIContext.queryParams,
      setQueryParams: NotificationsUIContext.setQueryParams,
    };
  }, [NotificationsUIContext]);

  useEffect(() => {
    let params = {
      page: NotificationsUIProps.queryParams.pageNumber,
      page_size: NotificationsUIProps.queryParams.pageSize,
      start_date: startDate,
      end_date: endDate,
    };
    dispatch(actions.fetchNotifications(params));
  }, [NotificationsUIProps.queryParams, startDate, endDate, dispatch]);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: SIZE_PER_PAGE_LIST,
    sizePerPage: NotificationsUIProps.queryParams.pageSize,
    page: NotificationsUIProps.queryParams.pageNumber,
    start_date: startDate,
    end_date: endDate,
    paginationTotalRenderer: (from, to, size) => customTotal(from, to, size, "notifications"),
  };

  return (
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination isLoading={listLoading} paginationProps={paginationProps}>
              {listLoading ? (
                <CircularLoader />
              ) : (
                <BootstrapTable
                  keyField="created_at"
                  wrapperClasses="table-responsive"
                  classes="table table-head-custom table-vertical-center"
                  bootstrap4
                  bordered={false}
                  remote
                  {...paginationTableProps}
                  onTableChange={getHandlerTableChange(NotificationsUIProps.setQueryParams)}
                  columns={columns}
                  data={notificationEntities.length == 0 ? [] : notificationEntities}
                  noDataIndication={() => (!listLoading ? <NoRecordsFoundMessage /> : "")}
                >
                  <PleaseWaitMessage />
                </BootstrapTable>
              )}
            </Pagination>
          );
        }}
      </PaginationProvider>
  );
}
